import React, { useMemo } from "react";
import styled from "styled-components";
import EmptyState from "../components/EmptyState";
import { fmtDateShort, daysUntil } from "../utils/formatters";
import theme from "../../../assets/theme";
import { PrimaryOutlinedCloseCTAButton } from "../../common/Buttons";

const SPACING = {
  xs: "4px",
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "24px",
  xxl: "32px",
  xxxl: "48px",
};

const RADIUS = {
  sm: "4px",
  md: "8px",
  lg: "12px",
  xl: "16px",
  full: "9999px",
};

const BREAKPOINTS = {
  xs: 320,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  xxl: 1536,
};

const media = {
  xs: (styles) => `@media (min-width: ${BREAKPOINTS.xs}px) { ${styles} }`,
  sm: (styles) => `@media (min-width: ${BREAKPOINTS.sm}px) { ${styles} }`,
  md: (styles) => `@media (min-width: ${BREAKPOINTS.md}px) { ${styles} }`,
  lg: (styles) => `@media (min-width: ${BREAKPOINTS.lg}px) { ${styles} }`,
  xl: (styles) => `@media (min-width: ${BREAKPOINTS.xl}px) { ${styles} }`,
  xxl: (styles) => `@media (min-width: ${BREAKPOINTS.xxl}px) { ${styles} }`,
  mobile: `@media (max-width: ${BREAKPOINTS.md - 1}px)`,
  tablet: `@media (min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`,
  desktop: `@media (min-width: ${BREAKPOINTS.lg}px)`,
};

const SelectBookingStage = ({ bookings = [], onSelect, onClose }) => {
  const eligibleBookings = useMemo(() => {
    return bookings
      .filter((b) => {
        const status = b.booking_status || b.status || "";
        const hearingDate = b.hearing_date || b.date;

        if (!hearingDate) return false;

        const dayDiff = daysUntil(hearingDate);
        const validStatus = ["confirmed", "reserved", "active"].includes(
          status,
        );

        return validStatus && dayDiff >= -3 && dayDiff <= 0;
      })
      .sort(
        (a, b) =>
          new Date(b.hearing_date || b.date) -
          new Date(a.hearing_date || a.date),
      );
  }, [bookings]);

  return (
    <Wrapper>
      <Intro>
        Showing bookings for today and the past 3 days. For older bookings, open
        them directly from your Bookings list.
      </Intro>

      {eligibleBookings.length === 0 ? (
        <EmptyState
          icon="📋"
          title="No eligible bookings"
          subtitle="On-site payments can only be recorded against recent bookings (today and last 3 days)."
        />
      ) : (
        <>
          <BookingList>
            {eligibleBookings.map((booking) => {
              const hearingDate = booking.hearing_date || booking.date;
              const roomName =
                booking.hearing_room || booking.room?.name || "Unknown Room";
              const ref = booking.booking_reference || booking.ref || "N/A";
              const startTime =
                booking.start_time?.slice(0, 5) || booking.startTime || "--:--";

              const claimant =
                booking.claimants_list?.[0]?.Name ||
                booking.participants?.claimants?.[0]?.name ||
                "Claimant";
              const respondent =
                booking.respondents_list?.[0]?.Name ||
                booking.participants?.respondents?.[0]?.name ||
                "Respondent";

              return (
                <BookingCard key={booking.id} onClick={() => onSelect(booking)}>
                  <BookingMeta>
                    <BookingTop>
                      <BookingRef>{ref}</BookingRef>
                      <BookingTime>
                        {fmtDateShort(hearingDate)} · {startTime}
                      </BookingTime>
                    </BookingTop>
                    <BookingDetails>
                      {roomName} · {claimant} vs. {respondent}
                    </BookingDetails>
                  </BookingMeta>
                  <Chevron>›</Chevron>
                </BookingCard>
              );
            })}
          </BookingList>

          <Footer>
            <PrimaryOutlinedCloseCTAButton
              style={{ width: "110px", padding: "12px 12px", borderRadius: 8 }}
              onClick={onClose}
            >
              Cancel
            </PrimaryOutlinedCloseCTAButton>
          </Footer>
        </>
      )}
    </Wrapper>
  );
};

export default SelectBookingStage;

const Wrapper = styled.div`
  padding: ${SPACING.xl};

  ${media.mobile} {
    padding: ${SPACING.lg};
  }
`;

const Intro = styled.p`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 ${SPACING.xl};
`;

const BookingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md};
  margin-bottom: ${SPACING.xl};

  scrollbar-width: thin;
  scrollbar-color: #d1d5db #f9fafb;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
    border-radius: ${RADIUS.md};
  }

  &::-webkit-scrollbar-thumb {
    // background: #d1d5db;
    border-radius: ${RADIUS.md};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

const BookingCard = styled.button`
  padding: ${SPACING.lg};
  border: 1.5px solid #e5e7eb;
  border-radius: ${RADIUS.lg};
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${SPACING.lg};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 4px;
    height: 100%;
    background: #00838c;
    transform: scaleY(0);
    transform-origin: top;
  }

  &:hover {
    border-color: #00838c;
    background: #f0fdfa;

    &::before {
      transform: scaleY(1);
    }
  }

  &:active {
    transform: translateY(1px);
  }

  ${media.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: ${SPACING.md};
  }
`;

const BookingMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const BookingTop = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.md};
  margin-bottom: ${SPACING.sm};
`;

const BookingRef = styled.span`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 14px;
  font-weight: 700;
  color: #111827;
`;

const BookingTime = styled.span`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #6b7280;
`;

const BookingDetails = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
`;

const Chevron = styled.span`
  color: #00838c;
  font-size: 20px;
  flex-shrink: 0;

  ${media.mobile} {
    align-self: flex-end;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${SPACING.md};

  ${media.mobile} {
    justify-content: stretch;
    gap: ${SPACING.sm};

    button {
      flex: 1;
    }
  }
`;
